/**
 * @fileoverview Top-level component / controller for working with Devices.
 */

import type { SlSplitPanel, SlTree, SlTreeItem } from '@shoelace-style/shoelace';
import { elt, removeAllChildren } from '../dom';
import { Placeholder } from '../components/placeholder';
import { fetchDevices, DeviceBase, ControllerDevice } from '../api/devices-api';


/** Device Data Model stuff that needs to move someplace that makes more sense. */
const INTERNAL_DEVICE_TYPES: Set<string> = new Set(["SIMULATOR", "GROOV_SYSTEM", "DATA_STORE", "EVENTS", "COMPUTED_TAGS"]);

function isInternal(device: DeviceBase) {
  return INTERNAL_DEVICE_TYPES.has(device.deviceType);
}

function isExternal(device: DeviceBase): boolean {
  return !INTERNAL_DEVICE_TYPES.has(device.deviceType);
}

function deviceName(device: DeviceBase): string {
  if (device.deviceType === 'OPTO_CONTROLLER') {
    return (device as ControllerDevice).strategyName;
  }
  else {
    return device.deviceName!;
  }
}

function compareDevice(a: DeviceBase, b: DeviceBase) {
  return deviceName(a).localeCompare(deviceName(b));
}

function internalDevices(devices: DeviceBase[]): DeviceBase[] {
  return devices.filter(isInternal).sort(compareDevice);
}

function externalDevices(devices: DeviceBase[]): DeviceBase[] {
  return devices.filter(isExternal).sort(compareDevice);
}

function explorerHeader(title: string, button?: HTMLElement): HTMLElement {
  const heading = elt('h2', {}, title);
  const header = elt('rebuild-explorer-header', {}, heading);

  if (button) {
    header.append(heading);
  }

  return header;
}

function deviceTreeItem(device: DeviceBase): SlTreeItem {
  let name = deviceName(device);
  const item = elt('sl-tree-item', {}, name);
  return item;
}

export class DevicesActivityPanel extends HTMLElement {
  static register() {
    if (!customElements.get('rebuild-devices-activity-panel')) {
      customElements.define('rebuild-devices-activity-panel', DevicesActivityPanel);
    }
  }

  static createElement(options?: any, ...body: any[]): DevicesActivityPanel {
    return elt('rebuild-devices-activity-panel', options, ...body);
  }

  splitPanel: SlSplitPanel;
  tree: SlTree;
  devices: DeviceBase[];

  constructor() {
    super();
    this.devices = [];
  }

  connectedCallback() {
    if (this.splitPanel) {
      // The DOM for this component has already been built up.
      return;
    }

    this.tree = elt('sl-tree');

    const explorer = elt('rebuild-explorer', { slot: 'start' },
      explorerHeader('Devices'), elt('rebuild-explorer-body', {}, this.tree));
    const right = Placeholder.createElement({ slot: 'end', title: 'Devices' });

    this.splitPanel = elt('sl-split-panel', {
      class: "w-full h-full",
      'position-in-pixels': 250
    }, explorer, right);

    this.append(this.splitPanel);

    // Load up the devices!
    fetchDevices()
      .then(devices => {
        this.devices = devices;
        this.rebuildTree();
      });
  }

  rebuildTree() {
    if (!this.tree) {
      return;
    }

    removeAllChildren(this.tree);
    const internals = internalDevices(this.devices);
    const externals = externalDevices(this.devices);

    for (let device of internals) {
      this.tree.append(deviceTreeItem(device));
    }

    if (internals.length > 0) {
      this.tree.append(elt('sl-divider'));
    }

    for (let device of externals) {
      this.tree.append(deviceTreeItem(device));
    }
  }
}